import axios, { CancelToken } from 'axios';
import store from '../store';
import { IChatMessage } from '../components/GroupChat.vue';
import { GroupChatConversationType } from '../models/GroupChat';
import { AiQueryTarget } from '../models/AiQueryTarget';
import { normalizeSupportCitations } from './SupportCitationParser';
import type { SupportVideoPlayback } from './SupportCitationParser';
import { withJsonErrorBody } from '../utils/ChatImageError';
import { normalizeProposedDeviceCommands } from './ProposedActionParser';
import type {
  ProposedActionConfirmation,
  ProposedActionDetail,
} from '../models/ProposedActionDetail';

export type {
  ProposedActionAmendment,
  ProposedActionChoice,
  ProposedActionConfirmation,
  ProposedActionDetail,
  ProposedActionField,
  ProposedActionTarget,
} from '../models/ProposedActionDetail';

export type {
  SupportCitation,
  SupportVideoPlayback,
} from './SupportCitationParser';

export type {
  ProposedDeviceCommand,
  ProposedDeviceCommandTarget,
} from './ProposedActionParser';

/**
 * The outcome of confirming a proposed device command.
 */
export interface ProposedActionResult {
  actionId: string;
  alreadyExecuted: boolean;
  commandQueueIds: number[];
  executedDateUtc: string | null;
}

/**
 * The outcome of canceling a proposed device command.
 */
export interface ProposedActionCancelResult {
  actionId: string;
  alreadyCancelled: boolean;
  cancelledDateUtc: string | null;
}

export interface ChatAttachment {
  blobName: string;
  originalName: string;
  contentType: string;
  sizeBytes: number;
}

export interface GroupChatParticipant {
  userId: string;
  userName: string;
}

export const chatReportReasons = [
  { label: 'Sexual content', value: 'SexualContent' },
  { label: 'Harassment or bullying', value: 'Harassment' },
  { label: 'Hate or discrimination', value: 'Hate' },
  { label: 'Violence or threats', value: 'Violence' },
  { label: 'Other', value: 'Other' },
] as const;

export type ChatReportReason = (typeof chatReportReasons)[number]['value'];

export interface ChatBlockPage {
  total: number;
  items: (GroupChatParticipant & { createdDateUtc: string })[];
}

export interface ChatVisibility {
  groupId?: number | null;
  isChatSuspended: boolean;
  visibleMessageIds: number[];
}

export function chatErrorMessage(error: unknown, fallback: string): string {
  const response = (
    error as { response?: { status?: number; data?: { code?: string } } }
  )?.response;
  switch (response?.data?.code) {
    case 'content_rejected':
      return 'This message was removed because it did not pass review.';
    case 'content_review_unavailable':
      return 'Content review is temporarily unavailable. Please try again later.';
    case 'chat_upload_unavailable':
      return 'The image could not be uploaded. Please try again.';
    case 'invalid_chat_image':
      return 'Choose a still JPEG or PNG image up to 4 MB, with dimensions from 50 to 7200 pixels.';
    case 'chat_suspended':
      return 'Your chat access is suspended. Your other features remain available.';
  }
  if (response?.status === 403 || response?.status === 404) {
    return 'This chat content or action is no longer available to you.';
  }
  return fallback;
}

export interface GroupChatConversation {
  groupChatConversationId: number;
  groupChatConversationType: GroupChatConversationType;
  title: string | null;
  createdDateUtc: string;
  participants: GroupChatParticipant[];
}

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_ROOT,
  withCredentials: false,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

export default {
  async reportMessage(
    messageId: number,
    reasonCode: ChatReportReason,
    notes: string
  ): Promise<void> {
    await apiClient.post(
      `/chat/messages/${messageId}/reports`,
      { reasonCode, notes },
      {
        headers: { Authorization: `Bearer ${store.getters.token}` },
      }
    );
  },

  async getBlocks(page = 0): Promise<ChatBlockPage> {
    const response = await apiClient.get('/chat/blocks', {
      params: { page, pageSize: 50 },
      headers: { Authorization: `Bearer ${store.getters.token}` },
    });
    return response.data;
  },

  async setBlocked(userId: string, blocked: boolean): Promise<void> {
    const url = `/chat/blocks/${encodeURIComponent(userId)}`;
    const config = {
      headers: { Authorization: `Bearer ${store.getters.token}` },
    };
    if (blocked) await apiClient.put(url, {}, config);
    else await apiClient.delete(url, config);
  },

  async getVisibility(messageIds: number[]): Promise<ChatVisibility> {
    const response = await apiClient.post(
      '/chat/visibility',
      { messageIds },
      {
        headers: { Authorization: `Bearer ${store.getters.token}` },
      }
    );
    return response.data;
  },

  /**
   * Fetches all chat conversations visible to the current user.
   */
  async getConversations(): Promise<{
    groupName: string;
    conversations: GroupChatConversation[];
  }> {
    const token = store.getters.token;
    const response = await apiClient.get('/chat/conversations', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  /**
   * Fetches same-group users that can be selected for a private chat.
   */
  async getParticipantCandidates(): Promise<GroupChatParticipant[]> {
    const token = store.getters.token;
    const response = await apiClient.get('/chat/participants/candidates', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  /**
   * Creates a fixed-participant private chat conversation.
   */
  async createConversation(payload: {
    participantUserIds?: string[];
    title?: string | null;
    isPropipeAi?: boolean;
  }): Promise<GroupChatConversation> {
    const token = store.getters.token;
    const response = await apiClient.post('/chat/conversations', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  /**
   * Removes the current user from a private chat conversation.
   */
  async leaveConversation(groupChatConversationId: number): Promise<void> {
    const token = store.getters.token;
    await apiClient.delete(
      `/chat/conversations/${groupChatConversationId}/participants/me`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  },

  /**
   * Fetches up to 20 chat messages for the current user's group.
   * Pass beforeId for keyset pagination to load older messages.
   */
  async getMessages(
    beforeId?: number,
    groupChatConversationId?: number | null
  ): Promise<{ groupName: string; messages: IChatMessage[] }> {
    const token = store.getters.token;
    const params: Record<string, number> = {};
    if (beforeId != null) {
      params.beforeId = beforeId;
    }
    const url = groupChatConversationId
      ? `/chat/conversations/${groupChatConversationId}/messages`
      : '/chat/messages';
    const response = await apiClient.get(url, {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = response.data;
    data.messages = data.messages.map(normalizeChatMessage);
    return data;
  },

  /**
   * Posts a new chat message. Returns the saved message with
   * server-assigned id and resolved userName.
   */
  async postMessage(
    text: string,
    attachment?: ChatAttachment | null,
    aiQueryTarget?: AiQueryTarget | null,
    groupChatConversationId?: number | null
  ): Promise<IChatMessage> {
    const token = store.getters.token;
    const body: Record<string, string | boolean | number | ChatAttachment> = {
      text,
    };
    if (attachment) {
      body.attachment = attachment;
    }
    if (aiQueryTarget) {
      body.aiQueryTarget = aiQueryTarget;
    }
    const url = groupChatConversationId
      ? `/chat/conversations/${groupChatConversationId}/messages`
      : '/chat/messages';
    const response = await apiClient.post(url, body, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return normalizeChatMessage(response.data);
  },

  /**
   * Soft-deletes a chat message by id. Only the original author
   * can delete their own messages.
   */
  async deleteMessage(id: number): Promise<void> {
    const token = store.getters.token;
    await apiClient.delete(`/chat/messages/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  /**
   * Toggles an emoji reaction on a message. Returns { added: boolean }.
   */
  async toggleReaction(
    messageId: number,
    emoji: string
  ): Promise<{ added: boolean }> {
    const token = store.getters.token;
    const response = await apiClient.post(
      `/chat/messages/${messageId}/reactions`,
      { emoji },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /**
   * Uploads a file attachment. Returns attachment metadata.
   */
  async uploadFile(file: File): Promise<ChatAttachment> {
    const token = store.getters.token;
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/chat/upload', formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Fetches a chat attachment as a blob URL that can be used as an img src.
   * The backend proxies the blob content with auth handled via Bearer header.
   */
  async getAttachmentUrl(
    blobName: string,
    preview = false,
    cancelToken?: CancelToken
  ): Promise<string> {
    const token = store.getters.token;
    try {
      const response = await apiClient.get('/chat/attachments', {
        params: { blobName, ...(preview ? { preview: true } : {}) },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        cancelToken,
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      throw await withJsonErrorBody(error);
    }
  },

  /**
   * Requests a five-minute PDF address after authenticated citation checks.
   */
  async getSupportSourceUrl(
    messageId: number,
    documentId: string
  ): Promise<string> {
    const token = store.getters.token;
    const response = await apiClient.get<{ url: string }>(
      `/chat/messages/${messageId}/sources/${encodeURIComponent(
        documentId
      )}/link`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data.url;
  },

  /**
   * Requests a short-lived playback address for a cited private support video.
   * The address is a credential: keep it in memory, never persist or log it.
   */
  async getSupportVideoPlayback(
    messageId: number,
    documentId: string
  ): Promise<SupportVideoPlayback> {
    const token = store.getters.token;
    const response = await apiClient.get(
      `/chat/messages/${messageId}/sources/${encodeURIComponent(
        documentId
      )}/playback`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /**
   * Reads everything a proposed device command would write.
   *
   * The server works this out from the devices as they are now, not from what
   * the AI saw, and returns a fingerprint of the result. Confirming without
   * having read it is refused.
   */
  async getProposedActionDetail(
    messageId: number,
    actionId: string
  ): Promise<ProposedActionDetail> {
    const token = store.getters.token;
    const response = await apiClient.get(
      `/chat/messages/${messageId}/actions/${encodeURIComponent(actionId)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /**
   * Re-reads the command with the operator's unconfirmed edits applied.
   *
   * Editing a setting changes what the command writes and therefore changes the
   * fingerprint, so the edited card has to be read back before it can be
   * confirmed. Nothing is sent or recorded by this call.
   */
  async previewProposedAction(
    messageId: number,
    actionId: string,
    confirmation: Omit<ProposedActionConfirmation, 'fingerprint'>
  ): Promise<ProposedActionDetail> {
    const token = store.getters.token;
    const response = await apiClient.post(
      `/chat/messages/${messageId}/actions/${encodeURIComponent(
        actionId
      )}/preview`,
      { fingerprint: '', ...confirmation },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /**
   * Confirms a device command that Propipe AI proposed.
   *
   * The body says what the operator read, not what to send. The server plans
   * the command again from current device state and refuses unless it arrives
   * at the same fingerprint, so nothing here decides what reaches the device.
   */
  async executeProposedAction(
    messageId: number,
    actionId: string,
    confirmation: ProposedActionConfirmation
  ): Promise<ProposedActionResult> {
    const token = store.getters.token;
    const response = await apiClient.post(
      `/chat/messages/${messageId}/actions/${encodeURIComponent(
        actionId
      )}/execute`,
      confirmation,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  /**
   * Cancels a device command that Propipe AI proposed.
   *
   * The server records the cancellation and posts a bot follow-up. Nothing is
   * queued. A second cancel of the same proposal is a no-op.
   */
  async cancelProposedAction(
    messageId: number,
    actionId: string
  ): Promise<ProposedActionCancelResult> {
    const token = store.getters.token;
    const response = await apiClient.post(
      `/chat/messages/${messageId}/actions/${encodeURIComponent(
        actionId
      )}/cancel`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
};

/** Normalizes optional collection fields on a chat message. */
export function normalizeChatMessage(msg: any): IChatMessage {
  msg.attachment = msg.attachment || null;

  msg.citations = normalizeSupportCitations(msg.citationJson, msg.citations);
  delete msg.citationJson;

  msg.proposedCommands = normalizeProposedDeviceCommands(
    msg.proposedActionJson,
    msg.proposedCommands
  );
  delete msg.proposedActionJson;
  msg.executedActionIds = Array.isArray(msg.executedActionIds)
    ? msg.executedActionIds
    : [];
  msg.cancelledActionIds = Array.isArray(msg.cancelledActionIds)
    ? msg.cancelledActionIds
    : [];
  return msg;
}
